/**
 * AI Chat Service - STT (Speech-to-Text) и TTS (Text-to-Speech) API
 *
 * Endpoints:
 * - POST /stt/v2 - конвертирует аудио в текст
 * - POST /tts/v2 - конвертирует текст в аудио (streaming WAV)
 * - POST /sequence-ai/ask - AI чат
 */

import { getConfig } from "./configService";
import { getAccessToken } from "./api";

// Базовый URL из конфига — бэкенд Атырау (`/api/v1`)
const getApiBaseUrlFromConfig = () => getConfig().API_URL;

// Базовый URL для AI Chat сервисов (STT, TTS)
const getAIChatBaseUrl = () => `${getApiBaseUrlFromConfig()}/ai_chat`;

// URL для AI Chat
export const getAIChatAskUrl = () => `${getApiBaseUrlFromConfig()}/sequence/chat/ask`;

// Для обратной совместимости (используется в логах ошибок)
export const AI_CHAT_ASK_URL = "sequence-ai/ask";

/**
 * Получить текущий base URL для STT/TTS (из конфига + /ai_chat)
 */
export function getApiBaseUrl(): string {
  return getAIChatBaseUrl();
}

// Типы для логирования
type LogType = "info" | "success" | "error" | "warning";
type LogCallback = (type: LogType, message: string, data?: any) => void;

// Глобальный callback для логирования (устанавливается из хука)
let globalLogCallback: LogCallback | null = null;

/**
 * Установить callback для логирования (вызывается из useAIAssistant)
 */
export function setLogCallback(callback: LogCallback | null): void {
  globalLogCallback = callback;
}

/**
 * Внутренняя функция логирования
 */
function log(type: LogType, message: string, data?: any): void {
  // Логируем в консоль
  const prefix =
    type === "error"
      ? "❌"
      : type === "success"
      ? "✅"
      : type === "warning"
      ? "⚠️"
      : "ℹ️";

  // Вызываем callback если установлен
  if (globalLogCallback) {
    globalLogCallback(type, message, data);
  }
}

// Типы ответов API
interface STTResponse {
  transcript: string;
  lang: string;
}

/**
 * Конвертирует Blob в base64 строку
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      // Убираем префикс "data:audio/webm;base64,"
      const base64Data = base64.split(",")[1] || base64;
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Опции для отправки сообщения в чат
export interface SendMessageOptions {
  signal?: AbortSignal;
  files?: File[];
  reasoningEffort?: "low" | "medium" | "high";
  context?: string;
  userId?: string;
}

/**
 * Отправляет текстовое сообщение в AI чат и возвращает ответ
 * @param query - текст вопроса
 * @param options - дополнительные опции (signal)
 * @returns Текст ответа от AI
 */
export async function sendMessage(
  query: string,
  options: SendMessageOptions = {}
): Promise<string> {
  const { signal } = options;
  // Используем URL для AI Chat из конфига
  const url = getAIChatAskUrl();
  const startTime = Date.now();

  // Отправляем JSON
  const body = JSON.stringify({ query });

  const token = getAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    accept: "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  console.log("🤖 AI Chat REQUEST →", url);
  log("info", `📤 AI Chat REQUEST → ${url}`, {
    request: {
      fullUrl: url,
      method: "POST",
      headers: headers,
      body: { query },
    },
  });

  try {
    const response = await fetch(url, {
      method: "POST",
      headers,
      body,
      signal,
    });

    const elapsed = Date.now() - startTime;
    const contentType = response.headers.get("content-type") || "";
    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    log("info", `📥 AI Chat RESPONSE ${response.status}`, {
      response: {
        fullUrl: url,
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        elapsed: `${elapsed}ms`,
      },
    });

    if (!response.ok) {
      let errorBody: any = null;
      try {
        if (contentType.includes("application/json")) {
          errorBody = await response.json();
        } else {
          errorBody = await response.text();
        }
      } catch {}

      log("error", `❌ AI Chat ERROR ${response.status} → ${url}`, {
        request: {
          fullUrl: url,
          method: "POST",
          headers: headers,
          body: { query },
        },
        response: {
          status: response.status,
          statusText: response.statusText,
          headers: responseHeaders,
          body: errorBody,
        },
        elapsed: `${elapsed}ms`,
      });

      throw new Error(
        `AI Chat failed: status=${response.status} | ${JSON.stringify(
          errorBody
        )}`
      );
    }

    // Парсим JSON ответ
    const data = await response.json();
    const answer = data?.answer || "";

    log("success", `✅ AI Chat SUCCESS (${elapsed}ms) → ${url}`, {
      request: {
        fullUrl: url,
        method: "POST",
        headers: headers,
        body: { query },
      },
      response: {
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        body: data,
      },
      answer: answer,
      elapsed: `${elapsed}ms`,
    });

    return answer;
  } catch (err: any) {
    const elapsed = Date.now() - startTime;

    if (err?.name === "AbortError") {
      log("warning", `💬 Chat: Запрос отменён → ${url}`, {
        request: { fullUrl: url, method: "POST", body: { query } },
        elapsed: `${elapsed}ms`,
      });
      throw new Error("AI Chat aborted by caller");
    }

    log("error", `❌ AI Chat EXCEPTION → ${url}`, {
      request: {
        fullUrl: url,
        method: "POST",
        headers: headers,
        body: { query },
      },
      error: {
        message: err?.message || String(err),
        name: err?.name,
        stack: err?.stack?.split("\n").slice(0, 5).join("\n"),
      },
      elapsed: `${elapsed}ms`,
    });

    throw new Error(`AI Chat network error: ${err?.message || String(err)}`);
  }
}

/**
 * Читает Server-Sent Events stream и вызывает callback для каждого чанка текста
 * Формат ответа:
 *   event: status
 *   data: Выбран контекст УК РК
 *
 *   event: answer
 *   data: "\u0422\u0435\u043a\u0441\u0442"
 *
 *   event: done
 *   data: [DONE]
 *
 * @param stream - ReadableStream от sendMessage
 * @param onChunk - callback для обработки каждого чанка текста (answer)
 * @param onDone - callback при завершении
 * @param onStatus - callback для статусных сообщений (опционально)
 */
export async function readStreamAsSSE(
  stream: ReadableStream<Uint8Array>,
  onChunk: (text: string) => void,
  onDone?: () => void,
  onStatus?: (status: string) => void
): Promise<string> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let fullText = "";
  let buffer = "";
  let currentEvent = ""; // Текущий тип события (status, answer, done)

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Разбиваем буфер на строки
      const lines = buffer.split("\n");
      buffer = lines.pop() || ""; // Сохраняем неполную строку

      for (const line of lines) {
        const trimmedLine = line.trim();

        // Пропускаем пустые строки
        if (!trimmedLine) {
          continue;
        }

        // Парсим event: xxx
        if (trimmedLine.startsWith("event:")) {
          currentEvent = trimmedLine.slice(6).trim();
          continue;
        }

        // Парсим data: xxx
        if (trimmedLine.startsWith("data:")) {
          const data = trimmedLine.slice(5).trim();

          // Обрабатываем в зависимости от типа события
          if (currentEvent === "done" || data === "[DONE]") {
            // Конец стрима
            continue;
          }

          if (currentEvent === "status") {
            // Статусное сообщение
            log("info", `📋 Status: ${data}`);
            onStatus?.(data);
            continue;
          }

          if (currentEvent === "answer" || !currentEvent) {
            // Текст ответа - может быть в JSON формате с Unicode escape
            let text = data;

            // Пробуем распарсить как JSON строку (для Unicode escape)
            if (data.startsWith('"') && data.endsWith('"')) {
              try {
                text = JSON.parse(data);
              } catch {
                // Если не удалось - используем как есть (без кавычек)
                text = data.slice(1, -1);
              }
            }

            if (text) {
              fullText += text;
              onChunk(text);
            }
          }
        }
      }
    }

    // Обрабатываем оставшийся буфер
    if (buffer.trim()) {
      const lines = buffer.split("\n");
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith("data:")) {
          const data = trimmedLine.slice(5).trim();
          if (data && data !== "[DONE]" && currentEvent === "answer") {
            let text = data;
            if (data.startsWith('"') && data.endsWith('"')) {
              try {
                text = JSON.parse(data);
              } catch {
                text = data.slice(1, -1);
              }
            }
            if (text) {
              fullText += text;
              onChunk(text);
            }
          }
        }
      }
    }

    onDone?.();
    return fullText;
  } finally {
    reader.releaseLock();
  }
}

/**
 * Speech-to-Text: отправляет аудио файл и получает текст
 * @param audioBlob - аудио данные (Blob)
 * @param lang - язык распознавания (по умолчанию 'ru')
 * @returns расшифрованный текст
 */
interface SpeechToTextOptions {
  signal?: AbortSignal;
}

export async function speechToText(
  audioBlob: Blob,
  lang: string = "ru",
  options: SpeechToTextOptions = {}
): Promise<string> {
  const { signal } = options;
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}/stt/v2`;
  const startTime = Date.now();

  // Конвертируем аудио в base64
  const audioBase64 = await blobToBase64(audioBlob);

  // JSON с base64 аудио
  const body = JSON.stringify({
    audio: audioBase64,
  });

  const token = getAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  console.log("🎤 STT REQUEST →", url);
  log("info", `📤 STT REQUEST → ${url}`, {
    request: {
      fullUrl: url,
      method: "POST",
      headers,
      body: {
        audioBase64Length: audioBase64.length,
        audioBlobSize: `${audioBlob.size} bytes`,
        audioBlobType: audioBlob.type,
      },
    },
  });

  try {
    const response = await fetch(url, {
      method: "POST",
      headers,
      body,
      signal,
    });

    const elapsed = Date.now() - startTime;
    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    log("info", `📥 STT RESPONSE ${response.status}`, {
      response: {
        fullUrl: url,
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        elapsed: `${elapsed}ms`,
      },
    });

    if (!response.ok) {
      let errorBody: any = null;
      try {
        errorBody = await response.text();
      } catch {}

      log("error", `❌ STT ERROR ${response.status} → ${url}`, {
        request: {
          fullUrl: url,
          method: "POST",
          body: { lang, audioSize: `${audioBlob.size} bytes` },
        },
        response: {
          status: response.status,
          statusText: response.statusText,
          headers: responseHeaders,
          body: errorBody,
        },
        elapsed: `${elapsed}ms`,
      });
      throw new Error(`STT API error: ${response.status} - ${errorBody}`);
    }

    const data: STTResponse = await response.json();
    log("success", `✅ STT SUCCESS (${elapsed}ms) → ${url}`, {
      request: {
        fullUrl: url,
        method: "POST",
        body: { lang, audioSize: `${audioBlob.size} bytes` },
      },
      response: {
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        body: data,
      },
      transcript: data.transcript,
      elapsed: `${elapsed}ms`,
    });

    return data.transcript;
  } catch (error: any) {
    const elapsed = Date.now() - startTime;
    const errorMessage = error?.message || String(error);

    // Определяем возможную причину ошибки
    let possibleCause = "";
    if (
      errorMessage.includes("Load failed") ||
      errorMessage.includes("Failed to fetch")
    ) {
      possibleCause =
        "Возможные причины: 1) CORS блокировка 2) Сертификат SSL 3) Сеть недоступна 4) URL неверный";
    } else if (errorMessage.includes("NetworkError")) {
      possibleCause = "Сетевая ошибка - проверьте подключение к интернету";
    } else if (errorMessage.includes("AbortError")) {
      possibleCause = "Запрос был отменён";
    }

    log("error", `❌ STT EXCEPTION → ${url}`, {
      request: {
        fullUrl: url,
        method: "POST",
        body: {
          lang,
          audioSize: `${audioBlob.size} bytes`,
          audioType: audioBlob.type,
        },
      },
      error: {
        message: errorMessage,
        name: error?.name,
        stack: error?.stack?.split("\n").slice(0, 5).join("\n"),
        possibleCause: possibleCause,
      },
      elapsed: `${elapsed}ms`,
      hint: "На iPad Safari часто 'Load failed' = проблема с HTTPS или CORS",
    });
    throw error;
  }
}

/**
 * Text-to-Speech: отправляет текст и получает аудио (streaming WAV)
 * @param text - текст для озвучивания
 * @returns аудио данные (Blob)
 */
export async function textToSpeech(text: string): Promise<Blob> {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}/tts/v2`;
  const startTime = Date.now();

  // JSON формат для tts/v2
  const body = JSON.stringify({ text });
  const token = getAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  console.log("🔊 TTS REQUEST →", url);
  log("info", `📤 TTS REQUEST → ${url}`, {
    request: {
      fullUrl: url,
      method: "POST",
      headers,
      body: { text: text, textLength: text.length },
    },
  });

  try {
    const response = await fetch(url, {
      method: "POST",
      headers,
      body,
    });

    const elapsed = Date.now() - startTime;
    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    log("info", `📥 TTS RESPONSE ${response.status}`, {
      response: {
        fullUrl: url,
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        elapsed: `${elapsed}ms`,
      },
    });

    if (!response.ok) {
      let errorBody: any = null;
      try {
        errorBody = await response.text();
      } catch {}

      log("error", `❌ TTS ERROR ${response.status} → ${url}`, {
        request: {
          fullUrl: url,
          method: "POST",
          body: {
            text: text,
            textLength: text.length,
          },
        },
        response: {
          status: response.status,
          statusText: response.statusText,
          headers: responseHeaders,
          body: errorBody,
        },
        elapsed: `${elapsed}ms`,
      });
      throw new Error(`TTS API error: ${response.status} - ${errorBody}`);
    }

    const contentType = response.headers.get("content-type") || "";
    let audioBlob: Blob;

    // Бэкенд возвращает JSON с base64 аудио: {"audio": "<base64>"}
    if (contentType.includes("application/json")) {
      const json = await response.json();

      if (!json.audio || typeof json.audio !== "string") {
        log("error", "❌ TTS: JSON не содержит audio", {
          response: JSON.stringify(json).slice(0, 500),
        });
        throw new Error(`TTS error: response missing audio field`);
      }

      // Декодируем base64 в байты
      const binaryString = atob(json.audio);
      const wavBytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0));

      log("info", "📦 TTS: Декодирован base64 WAV", {
        base64Length: json.audio.length,
        wavSize: wavBytes.length,
        firstBytes: Array.from(wavBytes.slice(0, 4))
          .map((b) => String.fromCharCode(b))
          .join(""),
        firstHex: Array.from(wavBytes.slice(0, 8))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join(" "),
      });

      // Проверяем RIFF header
      if (
        wavBytes[0] !== 0x52 ||
        wavBytes[1] !== 0x49 ||
        wavBytes[2] !== 0x46 ||
        wavBytes[3] !== 0x46
      ) {
        log("error", "❌ TTS: Не WAV данные", {
          hex: Array.from(wavBytes.slice(0, 20))
            .map((b) => b.toString(16).padStart(2, "0"))
            .join(" "),
        });
        throw new Error("TTS: данные не являются WAV");
      }

      audioBlob = new Blob([wavBytes], { type: "audio/wav" });
    } else {
      audioBlob = await response.blob();
    }

    log("success", `✅ TTS SUCCESS (${elapsed}ms) → ${url}`, {
      request: {
        fullUrl: url,
        method: "POST",
        body: { text: text, textLength: text.length },
      },
      response: {
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        body: {
          type: audioBlob.type,
          size: `${audioBlob.size} bytes`,
          extractedFromJson: contentType.includes("application/json"),
        },
      },
      elapsed: `${elapsed}ms`,
    });

    return audioBlob;
  } catch (error: any) {
    const elapsed = Date.now() - startTime;
    log("error", `❌ TTS EXCEPTION → ${url}`, {
      request: {
        fullUrl: url,
        method: "POST",
        body: { text: text, textLength: text.length },
      },
      error: {
        message: error?.message || String(error),
        name: error?.name,
        stack: error?.stack?.split("\n").slice(0, 5).join("\n"),
      },
      elapsed: `${elapsed}ms`,
    });
    throw error;
  }
}

// Глобальный AudioContext для разблокировки аудио
let audioContext: AudioContext | null = null;
let isAudioUnlocked = false;

/**
 * Разблокирует аудио воспроизведение (вызывать при user gesture)
 */
export function unlockAudio(): void {
  if (isAudioUnlocked) return;

  try {
    // Создаём AudioContext при первом взаимодействии
    audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)();

    // Воспроизводим тишину чтобы разблокировать
    const buffer = audioContext.createBuffer(1, 1, 22050);
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(audioContext.destination);
    source.start(0);

    isAudioUnlocked = true;
  } catch (e) {
    console.warn("⚠️ Не удалось разблокировать аудио:", e);
  }
}

/**
 * Парсит WAV header и возвращает информацию о формате
 */
function parseWavHeader(buffer: ArrayBuffer): {
  sampleRate: number;
  numChannels: number;
  bitsPerSample: number;
  dataOffset: number;
  dataSize: number;
} | null {
  const view = new DataView(buffer);

  // Проверяем RIFF header
  const riff = String.fromCharCode(
    view.getUint8(0),
    view.getUint8(1),
    view.getUint8(2),
    view.getUint8(3)
  );
  const wave = String.fromCharCode(
    view.getUint8(8),
    view.getUint8(9),
    view.getUint8(10),
    view.getUint8(11)
  );

  if (riff !== "RIFF" || wave !== "WAVE") {
    console.error("❌ Неверный WAV формат");
    return null;
  }

  // Ищем fmt chunk
  let offset = 12;
  let numChannels = 1;
  let sampleRate = 24000;
  let bitsPerSample = 16;
  let dataOffset = 0;
  let dataSize = 0;

  while (offset < buffer.byteLength - 8) {
    const chunkId = String.fromCharCode(
      view.getUint8(offset),
      view.getUint8(offset + 1),
      view.getUint8(offset + 2),
      view.getUint8(offset + 3)
    );
    const chunkSize = view.getUint32(offset + 4, true);

    if (chunkId === "fmt ") {
      const audioFormat = view.getUint16(offset + 8, true);
      numChannels = view.getUint16(offset + 10, true);
      sampleRate = view.getUint32(offset + 12, true);
      bitsPerSample = view.getUint16(offset + 22, true);
    } else if (chunkId === "data") {
      dataOffset = offset + 8;
      // Для streaming WAV dataSize может быть 0 - вычисляем реальный размер
      dataSize = chunkSize > 0 ? chunkSize : buffer.byteLength - dataOffset;
      break;
    }

    offset += 8 + chunkSize;
    // Align to even byte
    if (chunkSize % 2 !== 0) offset++;
  }

  // Если dataSize всё ещё 0, вычисляем из размера буфера
  if (dataSize === 0 && dataOffset > 0) {
    dataSize = buffer.byteLength - dataOffset;
  }

  return { sampleRate, numChannels, bitsPerSample, dataOffset, dataSize };
}

/**
 * Воспроизводит аудио blob через AudioContext (более надёжный метод)
 * @param audioBlob - аудио данные
 * @returns Promise, который резолвится когда аудио закончит воспроизведение
 */
export async function playAudio(audioBlob: Blob): Promise<void> {
  const startTime = Date.now();

  log("info", "▶️ playAudio: начало", {
    blobSize: `${audioBlob.size} bytes`,
    blobType: audioBlob.type,
  });

  try {
    // Создаём AudioContext если ещё не создан
    const ctx =
      audioContext ||
      new (window.AudioContext || (window as any).webkitAudioContext)();

    // Убедимся что контекст активен
    if (ctx.state === "suspended") {
      log("warning", "▶️ AudioContext suspended, resuming...", {
        state: ctx.state,
      });
      await ctx.resume();
      log("success", "▶️ AudioContext resumed", { state: ctx.state });
    }

    // Конвертируем Blob в ArrayBuffer
    log("info", "▶️ Конвертация blob в ArrayBuffer...");
    const arrayBuffer = await audioBlob.arrayBuffer();

    // Логируем первые байты чтобы понять формат
    const firstBytes = new Uint8Array(arrayBuffer.slice(0, 50));
    const firstBytesHex = Array.from(firstBytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join(" ");
    const firstBytesText = new TextDecoder().decode(firstBytes);

    log("info", "▶️ ArrayBuffer получен", {
      size: `${arrayBuffer.byteLength} bytes`,
      firstBytesHex: firstBytesHex,
      firstBytesText: firstBytesText.replace(/[^\x20-\x7E]/g, "."), // только printable символы
    });

    // Сначала пробуем стандартное декодирование
    log("info", "▶️ Попытка стандартного декодирования...");
    try {
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
      log("success", "▶️ Декодирование успешно!", {
        duration: `${audioBuffer.duration.toFixed(2)}s`,
        sampleRate: audioBuffer.sampleRate,
        channels: audioBuffer.numberOfChannels,
      });

      return playBuffer(ctx, audioBuffer, startTime);
    } catch (decodeError: any) {
      log("warning", "▶️ Стандартное декодирование не удалось", {
        error: decodeError?.message,
      });
      log("info", "▶️ Пробуем ручной парсинг WAV...");
    }

    // Fallback: ручной парсинг WAV
    const wavInfo = parseWavHeader(arrayBuffer);
    if (!wavInfo) {
      log("error", "▶️ Не удалось распарсить WAV");
      throw new Error("Не удалось распарсить WAV");
    }

    log("info", "▶️ WAV Info", wavInfo);

    // Извлекаем PCM данные
    const pcmData = new DataView(
      arrayBuffer,
      wavInfo.dataOffset,
      wavInfo.dataSize
    );
    const numSamples =
      wavInfo.dataSize / (wavInfo.bitsPerSample / 8) / wavInfo.numChannels;

    log("info", "▶️ Создание AudioBuffer вручную...", {
      numSamples,
      sampleRate: wavInfo.sampleRate,
    });

    // Создаём AudioBuffer
    const audioBuffer = ctx.createBuffer(
      wavInfo.numChannels,
      numSamples,
      wavInfo.sampleRate
    );

    // Заполняем данными
    for (let channel = 0; channel < wavInfo.numChannels; channel++) {
      const channelData = audioBuffer.getChannelData(channel);
      for (let i = 0; i < numSamples; i++) {
        const sampleIndex = i * wavInfo.numChannels + channel;
        let sample: number;

        if (wavInfo.bitsPerSample === 16) {
          sample = pcmData.getInt16(sampleIndex * 2, true) / 32768;
        } else if (wavInfo.bitsPerSample === 32) {
          sample = pcmData.getFloat32(sampleIndex * 4, true);
        } else {
          sample = (pcmData.getUint8(sampleIndex) - 128) / 128;
        }

        channelData[i] = sample;
      }
    }

    log("success", "▶️ AudioBuffer создан вручную", {
      duration: `${audioBuffer.duration.toFixed(2)}s`,
      sampleRate: audioBuffer.sampleRate,
    });

    return playBuffer(ctx, audioBuffer, startTime);
  } catch (error: any) {
    const elapsed = Date.now() - startTime;
    log("error", "▶️ Ошибка воспроизведения", {
      error: error?.message || String(error),
      elapsed: `${elapsed}ms`,
    });
    // Не бросаем ошибку - TTS опционален
  }
}

/**
 * Воспроизводит AudioBuffer
 */
function playBuffer(
  ctx: AudioContext,
  audioBuffer: AudioBuffer,
  startTime?: number
): Promise<void> {
  const source = ctx.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(ctx.destination);

  // Сохраняем ссылку для возможности остановки
  (window as any).__currentAudioSource = source;

  log("info", "▶️ Начало воспроизведения...", {
    duration: `${audioBuffer.duration.toFixed(2)}s`,
  });

  return new Promise((resolve) => {
    source.onended = () => {
      const elapsed = startTime ? Date.now() - startTime : 0;
      log("success", "▶️ Воспроизведение завершено!", {
        elapsed: startTime ? `${elapsed}ms` : "N/A",
      });
      (window as any).__currentAudioSource = null;
      resolve();
    };

    source.start(0);
  });
}

/**
 * Класс для записи аудио с микрофона
 */
export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | null = null;

  /**
   * Проверка поддержки MediaRecorder
   */
  static isSupported(): boolean {
    return !!(
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function" &&
      typeof window.MediaRecorder !== "undefined"
    );
  }

  /**
   * Запрос разрешения на микрофон
   */
  async requestPermission(): Promise<boolean> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000, // Оптимально для STT
        },
      });
      return true;
    } catch (error) {
      console.error("Microphone permission denied:", error);
      return false;
    }
  }

  /**
   * Начать запись
   */
  async start(): Promise<void> {
    if (!this.stream) {
      const hasPermission = await this.requestPermission();
      if (!hasPermission) {
        throw new Error("Нет доступа к микрофону");
      }
    }

    this.audioChunks = [];

    // Выбираем поддерживаемый формат
    const mimeType = this.getSupportedMimeType();

    this.mediaRecorder = new MediaRecorder(this.stream!, {
      mimeType,
    });

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.audioChunks.push(event.data);
      }
    };

    this.mediaRecorder.start(100); // Собираем данные каждые 100ms
  }

  /**
   * Остановить запись и получить аудио
   */
  stop(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error("Recorder not started"));
        return;
      }

      this.mediaRecorder.onstop = () => {
        const mimeType = this.mediaRecorder?.mimeType || "audio/webm";
        const audioBlob = new Blob(this.audioChunks, { type: mimeType });
        resolve(audioBlob);
      };

      this.mediaRecorder.onerror = (event) => {
        reject(event);
      };

      this.mediaRecorder.stop();
    });
  }

  /**
   * Освободить ресурсы
   */
  cleanup(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this.mediaRecorder = null;
    this.audioChunks = [];
  }

  /**
   * Получить поддерживаемый MIME тип
   */
  private getSupportedMimeType(): string {
    const types = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
      "audio/wav",
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return "audio/webm"; // fallback
  }
}
